import {
	handleKeyDownProps,
	handleResizeAnalyticsProps,
	handleResizeEndProps,
	handleResizeProps,
	handleResizeStartProps,
	handleTransitionEndProps,
} from './Sidebar.types'

import { sendAnalytics } from '@audio-network/analytics'

export const MINIMUM_COLLAPSED_WIDTH = 56
export const MINIMUM_EXPANDED_WIDTH = 200

const handleResizeAnalytics = ({
	clientX,
	expanded,
	widthRef,
}: handleResizeAnalyticsProps): void => {
	let width: number

	/**
	 * If the user is dragging we'll receive a clientX here
	 */
	if (clientX) {
		width =
			clientX >= MINIMUM_EXPANDED_WIDTH ? clientX : MINIMUM_COLLAPSED_WIDTH
	} else {
		const expandedWidth = widthRef.current ?? MINIMUM_EXPANDED_WIDTH

		width = !expanded ? expandedWidth : MINIMUM_COLLAPSED_WIDTH
	}

	sendAnalytics({
		noun: 'Sidebar',
		pastTenseVerb: 'Resized',
		eventParameters: {
			Width: width,
		},
	})
}

export const handleKeyDown = (
	event: React.KeyboardEvent<HTMLDivElement>,
	{ expanded, toggleExpanded, widthRef }: handleKeyDownProps
): void => {
	const { key } = event

	if (key === 'Enter' || key === ' ') {
		toggleExpanded(!expanded)
	}

	if (key === 'ArrowLeft' && expanded) {
		toggleExpanded(false)
	}

	if (key === 'ArrowRight' && !expanded) {
		toggleExpanded(true)
	}

	handleResizeAnalytics({
		clientX: 0,
		expanded,
		widthRef,
	})
}

const handleResize = (
	event: MouseEvent,
	{ sidebarContentRef, resizingRef }: handleResizeProps
): void => {
	resizingRef.current = true

	if (event.clientX) {
		sidebarContentRef.current.style.width = `${event.clientX}px`
	}
}

/**
 * Store event handlers at module scope to prevent unnecessary re-creation
 * of event listeners. This ensures we can properly remove the same handler
 * functions that were originally attached.
 */
let mouseMoveHandler: ((event: MouseEvent) => void) | null = null
let mouseEndHandler: ((event: MouseEvent) => void) | null = null

const handleResizeEnd = (
	event: MouseEvent,
	{
		resizingRef,
		expanded,
		toggleExpanded,
		sidebarContentRef,
		widthRef,
	}: handleResizeEndProps
): void => {
	const { clientX } = event

	if (mouseMoveHandler) {
		document.body.removeEventListener('mousemove', mouseMoveHandler)
	}
	if (mouseEndHandler) {
		document.body.removeEventListener('mouseleave', mouseEndHandler)
		document.body.removeEventListener('mouseup', mouseEndHandler)
	}

	// Reset the styling
	document.body.style.cursor = null
	document.body.style.userSelect = null
	document.body.style['-webkit-user-select'] = null

	sidebarContentRef.current.style.paddingRight = null
	sidebarContentRef.current.style.transition = null

	/** Shrink the sidebar */
	if (!resizingRef.current && expanded) {
		toggleExpanded(false)
	}

	/** Expand the sidebar */
	if (!resizingRef.current && !expanded) {
		toggleExpanded(true)
	}

	/** Update the width, so long as the sidebar is being resized and the width is greater than the minimum expanded width */
	if (resizingRef.current && event.clientX >= MINIMUM_EXPANDED_WIDTH) {
		widthRef.current = clientX
	}

	/** Shrink the sidebar, so long as the sidebar is being resized and the width is less than the minimum expanded width */
	if (resizingRef.current && clientX < MINIMUM_EXPANDED_WIDTH) {
		toggleExpanded(false)
	}

	/** Expand the sidebar, so long as the sidebar is being resized and the width is greater than the minimum expanded width */
	if (resizingRef.current && clientX >= MINIMUM_EXPANDED_WIDTH) {
		sidebarContentRef.current.style.overflow = null
		toggleExpanded(true)
	}

	handleResizeAnalytics({
		clientX: resizingRef.current && clientX,
		expanded,
		widthRef,
	})

	resizingRef.current = false

	// Clean up the handlers
	mouseMoveHandler = null
	mouseEndHandler = null
}

export const handleResizeStart = ({
	sidebarContentRef,
	resizingRef,
	expanded,
	toggleExpanded,
	widthRef,
}: handleResizeStartProps): void => {
	resizingRef.current = false

	// Use handlers from the module level to maintain the same function reference
	mouseMoveHandler = (event: MouseEvent): void =>
		handleResize(event, { sidebarContentRef, resizingRef })
	mouseEndHandler = (event: MouseEvent): void =>
		handleResizeEnd(event, {
			resizingRef,
			expanded,
			toggleExpanded,
			sidebarContentRef,
			widthRef,
		})

	// Use the stored handler functions when adding listeners
	if (mouseMoveHandler) {
		document.body.addEventListener('mousemove', mouseMoveHandler)
	}
	if (mouseEndHandler) {
		document.body.addEventListener('mouseleave', mouseEndHandler)
		document.body.addEventListener('mouseup', mouseEndHandler)
	}

	/** Set the cursor to the resize cursor, prevent user selection and scrolling */
	document.body.style.cursor = 'ew-resize'
	document.body.style.userSelect = 'none'
	document.body.style['-webkit-user-select'] = 'none'

	/** Adjust sidebar styling to prepare for resizing */
	sidebarContentRef.current.style.overflow = 'hidden'
	sidebarContentRef.current.style.paddingRight = '0'
	sidebarContentRef.current.style.transition = 'unset'
}

export const handleTransitionEnd = (
	event: React.TransitionEvent<HTMLDivElement>,
	{ sidebarContentRef }: handleTransitionEndProps
): void => {
	event.stopPropagation()

	/** Reset the sidebar styling */
	sidebarContentRef.current.style.overflow = null
	sidebarContentRef.current.style.paddingRight = null
}

export const stopLogoPropagation = (event: Event): void => {
	event.stopPropagation()
}
